import stringify from 'json-stable-stringify'
import { Action, Middleware, Reducer } from 'redux'
import { PersistConfig, persistReducer } from 'redux-persist'

import autoMergeLevel2 from './autoMergeLevel2'

// Types and interfaces
type PersistScopedConfig<S, RS = any, HSS = any, ESS = any, HydrationCTX = any> = Omit<
  PersistConfig<ScopedState<S>, RS, HSS, ESS>,
  'whitelist' | 'blacklist'
> & {
  mergeFn?: (
    inboundState: ScopedState<S>,
    reducedState: ScopedState<S>,
    hydrationContext?: HydrationCTX,
  ) => ScopedState<S>
}

type PersistScoped = {
  _currentScope?: {
    id: string
    encryptionKey: string
  }
  _scopes: Record<string, string>
}

type ScopedState<S> = S & PersistScoped

type KeysMatching<T, V> = {
  [K in keyof T]: T[K] extends V ? K : never
}[keyof T]

type ScopedStoreKeys<S> = KeysMatching<S, PersistScoped>
type InnerStoreState<S, K extends ScopedStoreKeys<S>> = S[K] extends PersistScoped
  ? Omit<S[K], '_scopes' | '_currentScope'>
  : S[K]
type MiddlewareConfig<S, K extends ScopedStoreKeys<S> & string, HydrationCTX> = {
  getScopeKey: (state: S) => string | undefined
  getEncryptionKey: (state: S) => string | undefined
  transformState?: (state: InnerStoreState<S, K>, hydrationContext?: HydrationCTX) => Record<string, any>
  getHydrationContext?: (state: S) => HydrationCTX
  shouldPersist?: (state: S) => boolean
}
const IV_LENGTH = 12
const CHUNK_SIZE = 64 * 1024 // 64KB chunks
const MAX_PARALLEL_DECRYPT = 8

// -- Base64 Utilities --

function base64UrlToBase64(base64Url: string): string {
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const padLength = (4 - (base64.length % 4)) % 4
  base64 += '='.repeat(padLength)
  return base64
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

function uint8ArrayToBase64(array: Uint8Array): Promise<string> {
  // Use Blob + FileReader to avoid call stack issues
  const blob = new Blob([array])
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (!reader.result || typeof reader.result !== 'string') {
        reject(new Error('Failed to read as data URL'))
        return
      }
      const base64Data = reader.result.split(',')[1]
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// -- Key Import --

async function importKeyFromBase64(base64Key: string): Promise<CryptoKey> {
  const fixedBase64 = base64UrlToBase64(base64Key)
  const rawKey = base64ToUint8Array(fixedBase64)
  return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

// -- Encryption --
async function encryptData(data: string, base64Key: string, existingEncryptedBase64?: string): Promise<string> {
  let iv: Uint8Array

  if (existingEncryptedBase64) {
    const existingBytes = base64ToUint8Array(existingEncryptedBase64)
    iv = existingBytes.slice(0, IV_LENGTH)
  } else {
    iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  }

  const cryptoKey = await importKeyFromBase64(base64Key)
  const encoder = new TextEncoder()
  const chunks: Uint8Array[] = []

  for (let i = 0; i < data.length; i += CHUNK_SIZE) {
    const chunk = data.slice(i, i + CHUNK_SIZE)
    const encoded = encoder.encode(chunk)

    const chunkIv = new Uint8Array(IV_LENGTH)
    chunkIv.set(iv.slice(0, IV_LENGTH - 4))
    chunkIv.set(new Uint8Array(new Uint32Array([i / CHUNK_SIZE]).buffer), IV_LENGTH - 4)

    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: chunkIv }, cryptoKey, encoded)
    chunks.push(new Uint8Array(ciphertext))
  }

  const chunkCount = chunks.length
  const totalDataSize = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const headerSize = IV_LENGTH + 4 + 4 * chunkCount
  const combined = new Uint8Array(headerSize + totalDataSize)

  let offset = 0

  // Write IV
  combined.set(iv, offset)
  offset += IV_LENGTH

  // Write chunk count
  new DataView(combined.buffer, offset, 4).setUint32(0, chunkCount, true)
  offset += 4

  // Write chunk sizes
  for (const chunk of chunks) {
    new DataView(combined.buffer, offset, 4).setUint32(0, chunk.length, true)
    offset += 4
  }

  // Write chunk data
  for (const chunk of chunks) {
    combined.set(chunk, offset)
    offset += chunk.length
  }

  return await uint8ArrayToBase64(combined)
}

// -- Decryption --

async function decryptData(base64: string, base64Key: string): Promise<string> {
  const cryptoKey = await importKeyFromBase64(base64Key)
  const bytes = base64ToUint8Array(base64)

  let offset = 0

  // Read IV
  const iv = bytes.slice(offset, offset + IV_LENGTH)
  offset += IV_LENGTH

  // Read chunk count
  const chunkCount = new DataView(bytes.buffer, offset, 4).getUint32(0, true)
  offset += 4

  // Read chunk sizes
  const chunkSizes: number[] = []
  for (let i = 0; i < chunkCount; i++) {
    const size = new DataView(bytes.buffer, offset, 4).getUint32(0, true)
    chunkSizes.push(size)
    offset += 4
  }

  const decryptedChunks = Array.from<string>({ length: chunkCount })
  const decoder = new TextDecoder()

  let i = 0
  while (i < chunkCount) {
    const batch = []
    for (let j = 0; j < MAX_PARALLEL_DECRYPT && i + j < chunkCount; j++) {
      const index = i + j
      const chunkSize = chunkSizes[index]
      const ciphertext = bytes.slice(offset, offset + chunkSize)
      offset += chunkSize

      const chunkIv = new Uint8Array(IV_LENGTH)
      chunkIv.set(iv.slice(0, IV_LENGTH - 4))
      chunkIv.set(new Uint8Array(new Uint32Array([index]).buffer), IV_LENGTH - 4)

      const promise = crypto.subtle
        .decrypt({ name: 'AES-GCM', iv: chunkIv }, cryptoKey, ciphertext)
        .then((decrypted) => {
          decryptedChunks[index] = decoder.decode(decrypted)
        })

      batch.push(promise)
    }

    await Promise.all(batch)
    i += MAX_PARALLEL_DECRYPT
  }

  return decryptedChunks.join('')
}
const persistScopedPrefix = 'persistScoped'
const HYDRATE_SCOPE_ACTION = 'persistScoped/hydrateScopeAction'
const UPDATE_SCOPE_ACTION = 'persistScoped/updateScopeAction'
type HydrateScopeAction = {
  type: typeof HYDRATE_SCOPE_ACTION
  payload: {
    storeName: string
    scopeId: string
    decryptedState: object
    encryptionKey: string
    hydrationContext: any
  }
}
type UpdateScopeAction = {
  type: typeof UPDATE_SCOPE_ACTION
  payload: {
    storeName: string
    scopeId: string
    encryptedState: string
    encryptionKey: string
  }
}

const isDecryptingMap = new Map<string, boolean>()
function createDecryptKey({
  storeName,
  scopeId,
  encryptionKey,
}: {
  storeName: string
  scopeId: string
  encryptionKey: string
}) {
  return `${persistScopedPrefix}/${storeName}/decrypt/${scopeId}/${encryptionKey}`
}
export function createPersistScopedReducerMiddleware<S>() {
  return function <K extends ScopedStoreKeys<S> & string, HydrationCTX>(
    scopedStore: K,
    config: MiddlewareConfig<S, K, HydrationCTX>,
  ): Middleware<any, S> {
    return (store) => (next) => async (action) => {
      const before = store.getState()
      const result = next(action)
      const after = store.getState()

      // Ignore if the scoped store did not change
      if (before[scopedStore] === after[scopedStore]) {
        return result
      }

      // Ignore our own actions
      if (action.type.startsWith(persistScopedPrefix)) {
        return result
      }

      const reducerState = after[scopedStore] as ScopedState<S[K]>
      const scopeId = config.getScopeKey(after)
      const encryptionKey = config.getEncryptionKey(after)
      // Ignore if we don't have a scope or encryption key
      if (!scopeId || !encryptionKey) {
        return result
      }

      if (config.shouldPersist && !config.shouldPersist(after)) {
        return result
      }

      let hydrationContext: HydrationCTX | undefined
      if (config.getHydrationContext) {
        hydrationContext = config.getHydrationContext?.(after)
        if (!hydrationContext) {
          return result
        }
      }

      if (scopeId !== reducerState._currentScope?.id || encryptionKey !== reducerState._currentScope?.encryptionKey) {
        const decryptKey = createDecryptKey({ storeName: scopedStore, scopeId, encryptionKey })
        if (isDecryptingMap.get(decryptKey)) {
          return result
        }
        isDecryptingMap.set(decryptKey, true)
        // Scope changed, hydrate the new scope
        const hasEncryptedState = reducerState._scopes && scopeId in reducerState._scopes
        let decryptedState: object = {}
        if (hasEncryptedState) {
          const encryptedState = reducerState._scopes[scopeId]
          try {
            const decryptedStateStr = await decryptData(encryptedState, encryptionKey)
            decryptedState = JSON.parse(decryptedStateStr)
          } catch {}
        }
        const hydrateAction: HydrateScopeAction = {
          type: `persistScoped/hydrateScopeAction`,
          payload: {
            storeName: scopedStore,
            scopeId,
            decryptedState,
            encryptionKey,
            hydrationContext,
          },
        }
        store.dispatch(hydrateAction)
        isDecryptingMap.set(decryptKey, false)
      } else {
        try {
          // Scope did not change, update the encrypted state
          const stateToStore: Partial<ScopedState<S[K]>> & { _persist?: any } = { ...reducerState }
          delete stateToStore._currentScope
          delete stateToStore._scopes
          const innerState = config.transformState
            ? config.transformState(stateToStore as InnerStoreState<S, K>, hydrationContext)
            : stateToStore
          // Reuse the existing encrypted state IV if exists for comparison
          const encryptedState = await encryptData(stringify(innerState), encryptionKey, reducerState._scopes[scopeId])
          if (encryptedState === reducerState._scopes[scopeId]) {
            return result
          }
          const updateAction: UpdateScopeAction = {
            type: `persistScoped/updateScopeAction`,
            payload: { storeName: scopedStore, scopeId, encryptedState, encryptionKey },
          }
          store.dispatch(updateAction)
        } catch {}
      }

      return result
    }
  }
}

// Reducer

function isHydrateScopeAction(action: Action): action is HydrateScopeAction {
  return action.type === 'persistScoped/hydrateScopeAction'
}

function isUpdateScopeAction(action: Action): action is UpdateScopeAction {
  return action.type === 'persistScoped/updateScopeAction'
}

export default function persistScopedReducer<S, A extends Action>(
  persistConfig: PersistScopedConfig<S>,
  baseReducer: Reducer<S, A>,
): Reducer<ScopedState<S>, A | HydrateScopeAction | UpdateScopeAction> {
  const config = {
    ...persistConfig,
    // Only persist the encrypted scopes
    whitelist: ['_scopes'],
  }

  return persistReducer<ScopedState<S>, A | HydrateScopeAction | UpdateScopeAction>(config, (state, action) => {
    const nextState = baseReducer(state, action as A)
    // Handle initial state
    if (!state) {
      return {
        ...nextState,
        _scopes: {},
      }
    }

    // Handle hydrate action if this is the store we are managing
    if (isHydrateScopeAction(action) && action.payload.storeName === persistConfig.key) {
      const mergeFn = persistConfig.mergeFn ?? autoMergeLevel2<ScopedState<S>>
      const result = mergeFn(
        action.payload.decryptedState as ScopedState<S>,
        nextState as ScopedState<S>,
        action.payload.hydrationContext,
      )
      return {
        ...result,
        _currentScope: {
          id: action.payload.scopeId,
          encryptionKey: action.payload.encryptionKey,
        },
        _scopes: state._scopes,
      }
      // Handle update action if this is the store we are managing
    } else if (isUpdateScopeAction(action) && action.payload.storeName === persistConfig.key) {
      return {
        ...nextState,
        _currentScope: {
          id: action.payload.scopeId,
          encryptionKey: action.payload.encryptionKey,
        },
        _scopes: {
          ...state._scopes,
          [action.payload.scopeId]: action.payload.encryptedState,
        },
      }
    }

    // If the state did not change, return the original state
    if (state === nextState) {
      return state
    }

    // Otherwise, return the next state with the scopes and current scope
    return {
      ...nextState,
      _scopes: state._scopes,
      _currentScope: state._currentScope,
    }
  })
}
